/** @jsx jsx */
import * as React from 'react';
import { jsx } from '@compiled/react';
import { isDatePickerSupportEnabled, isDueDateSupportEnabled } from '@helpcenter/feature-flags';
import type { FieldFormProps } from '@helpcenter/field-types';
import { parseISO, isValid as isValidDate, formatISO, parse as dateFnsParse } from 'date-fns';
import { isEmpty } from 'lodash';
import type { WrappedComponentProps } from 'react-intl-next';
import { injectIntl } from 'react-intl-next';
import { FieldBase } from 'view/common/fields/field-base';
import messages from 'view/common/messages';
import { css } from '@atlaskit/css';
import { DatePicker } from '@atlaskit/datetime-picker';
import FeatureGates from '@atlaskit/feature-gate-js-client';
import { operationalEvent } from '@atlassian/help-center-common-util/analytics/events';
import dateNow from '@atlassian/help-center-common-util/date-now';
import { format, getWeekStartDay } from '@atlassian/help-center-common-util/date-picker-utils';
import { componentWithCondition } from '@atlassian/help-center-common-util/feature-flags-utils';
import { withControlledFormComponentValue } from '../../with-controlled-form-component-value';

export interface DatePickerProps extends FieldFormProps<string>, WrappedComponentProps {
    dateFormat?: string;
    defaultValue?: string;
    firstDay?: number;
    useISO8601WeekNumbers?: boolean;
    locale: string;
    value?: string;
}

const IsoDateFormat = /^\d{4}-\d{2}-\d{2}$/;

/**
 * Processes a date value by handling ISO format and applying necessary transformations
 * @param value - The input date string to process
 *                 Input formats:
 *                 - ISO date format: "YYYY-MM-DD" (e.g., "2023-12-25")
 *                 - JavaScript parseable date strings (e.g., "Dec 25, 2023", "2023/12/25")
 *                 - undefined or null values
 * @returns Processed date string in ISO 8601 format (e.g., "2023-12-25T00:00:00.000Z")
 *          or undefined if the input is invalid or cannot be parsed
 */
export const processDateValue = (value: string | undefined): string | undefined => {
    try {
        if (!value || !isValidDate(new Date(value))) {
            return undefined;
        }

        // Fix for the one-day-off issue with new Date()
        // Link: [DateTime Picker One Day Off Issue](https://hello.atlassian.net/wiki/x/zwTjxw)
        const processedValue = value.match(IsoDateFormat)
            ? formatISO(new Date(value.replace(/-/g, '/')))
            : formatISO(new Date(value));

        // Fix for range time error when the string date passed to the date picker is not valid
        if (FeatureGates.checkGate('fix_masked_sentry_issues')) {
            return isValidDate(new Date(processedValue)) ? processedValue : undefined;
        }
        return processedValue;
    } catch (error) {
        operationalEvent({
            action: `processDateValueFailed`,
            actionSubject: 'datePicker',
            attributes: {
                inputValue: value,
                error: error instanceof Error ? error.message : 'unknown',
            },
        });
        return undefined;
    }
};

// TODO: Add proper locale support so that this workaround to fix date parsing isn't needed
const parseInputValue = (date: string): Date => {
    if (isValidDate(new Date(date))) {
        return parseISO(new Date(date).toISOString());
    }
    return parseISO(new Date().toISOString());
};

export const parseInputValueWithDateFormat = (date: string, dateFormat: string): Date => {
    let parsedDate: Date | undefined;
    try {
        parsedDate = dateFnsParse(date, dateFormat, new Date());
    } catch (error) {
        // fire operational event with step information
        operationalEvent({
            action: `dateParseFailed`,
            actionSubject: 'datePicker',
            attributes: {
                dateFormat,
                inputValue: date,
                error: error instanceof Error ? error.message : 'unknown',
            },
        });
    }

    if (parsedDate && isValidDate(parsedDate)) {
        return parsedDate;
    }

    if (isValidDate(new Date(date))) {
        return parseISO(new Date(date).toISOString());
    }

    return parseISO(new Date().toISOString());
};

export class CustomDatePicker extends React.PureComponent<DatePickerProps> {
    onChange = (isoTime: string) => {
        const { onChange } = this.props;
        if (isDueDateSupportEnabled()) {
            onChange &&
                onChange(this.props.id, isoTime, false, {
                    rawObject: {},
                    rawValue: isoTime,
                });
        } else {
            onChange && onChange(this.props.id, isoTime, false);
        }
    };

    parseDateLabel = (isoTime: string, dateFormat: string): string => {
        return isoTime && dateFormat ? format(parseISO(isoTime), dateFormat) : isoTime;
    };

    render() {
        const {
            id,
            label,
            required,
            defaultValue,
            description,
            errorMessage,
            disabled,
            locale,
            firstDay,
            useISO8601WeekNumbers,
            value,
            intl,
            dateFormat = 'yyyy-MM-dd',
        } = this.props;
        const isInvalid = !isEmpty(errorMessage);
        // Need to refer to id like this.
        // See: https://atlaskit.atlassian.com/packages/core/datetime-picker
        const inputId = `react-select-${id}-input`;
        const defaultDateValue = processDateValue(defaultValue);
        const processedValue = value && isDueDateSupportEnabled() ? processDateValue(value) : undefined;

        return (
            <FieldBase
                htmlFor={inputId}
                label={label}
                required={required}
                description={description}
                errorMessage={errorMessage}
            >
                <div css={datePickerContainerCss}>
                    <DatePicker
                        id={inputId}
                        name={id}
                        locale={locale}
                        defaultValue={defaultDateValue}
                        onChange={this.onChange}
                        isInvalid={isInvalid}
                        isDisabled={!!disabled}
                        formatDisplayLabel={this.parseDateLabel}
                        placeholder={intl.formatMessage(messages.forExample, { text: format(dateNow(), dateFormat) })}
                        dateFormat={dateFormat}
                        weekStartDay={getWeekStartDay(firstDay, useISO8601WeekNumbers)}
                        parseInputValue={
                            FeatureGates.checkGate('fix_date_picker_format_bug')
                                ? parseInputValueWithDateFormat
                                : parseInputValue
                        }
                        aria-describedby={`${inputId}-field-description`}
                        value={isDueDateSupportEnabled() ? processedValue : undefined}
                    />
                </div>
            </FieldBase>
        );
    }
}

const wrappedDatePicker = injectIntl(CustomDatePicker);

export default wrappedDatePicker;

export const ControlledDatePicker = withControlledFormComponentValue((_field: string, value: string) => value)(
    wrappedDatePicker
);

export const ConditionalDatepicker = componentWithCondition(
    () => isDatePickerSupportEnabled(),
    ControlledDatePicker,
    wrappedDatePicker
);

const datePickerContainerCss = css({
    width: '160px', // grid.multiple(20).px
});
